/**
 * Visible text (decision 41; part 04 log, table V): each paragraph, heading and centered block as the writer
 * sees it, with no markup or escapes, and a map from every visible character back to the source it comes
 * from. Find and replace searches this text and edits the source through the map, so "1945." finds `1945\.`
 * and "very good" finds `*very* good`; a match never crosses a block.
 */
import { indexFootnotes } from '../parser/footnote-index.ts';
import type { ParsedChapter } from '../parser/parse.ts';
import { rawPieces } from './inline.ts';
import type { Inline } from './inline.ts';
import { chapterModel } from './model.ts';
import type { Block } from './model.ts';

/** What an image shows as in visible text: one object replacement character. */
export const IMAGE_CHARACTER = '￼';

/** One block's visible text. */
export interface VisibleBlock {
  readonly kind: 'paragraph' | 'heading' | 'centered';
  /** The block in the source. */
  readonly from: number;
  readonly to: number;
  readonly text: string;
  /**
   * The source span of visible character `i` is `spans[2 * i]` to `spans[2 * i + 1]`. Every span is
   * non-empty, and starts and ends each never decrease; a character made of several source characters (an
   * escape, a reference, a break) maps to all of them, and every digit of a note number to the reference.
   */
  readonly spans: Int32Array;
}

/** Accumulates one block's text and spans. */
class Collector {
  private readonly parts: string[] = [];
  /** The spans so far, two per unit, in a buffer that grows by doubling (kept from block to block). */
  private spans = new Int32Array(256);
  private length = 0;
  private readonly numbers: ReadonlyMap<number, number | null>;

  /** `numbers` maps each footnote reference's start to its number, or null for an orphan. */
  constructor(numbers: ReadonlyMap<number, number | null>) {
    this.numbers = numbers;
  }

  /** `text`, every UTF-16 unit of it mapped to `from`–`to`. */
  add(text: string, from: number, to: number): void {
    this.parts.push(text);
    const spans = this.room(text.length);
    for (let left = text.length; left > 0; left -= 1) {
      spans[this.length] = from;
      spans[this.length + 1] = to;
      this.length += 2;
    }
  }

  /** `text`, unit `i` mapped to `from + i` (a literal run). */
  literal(text: string, from: number): void {
    this.parts.push(text);
    const spans = this.room(text.length);
    for (let i = 0; i < text.length; i += 1) {
      spans[this.length] = from + i;
      spans[this.length + 1] = from + i + 1;
      this.length += 2;
    }
  }

  /** The span buffer, with room for `units` more units' spans. */
  private room(units: number): Int32Array {
    const needed = this.length + 2 * units;
    if (needed > this.spans.length) {
      let size = this.spans.length * 2;
      while (size < needed) size *= 2;
      const grown = new Int32Array(size);
      grown.set(this.spans.subarray(0, this.length));
      this.spans = grown;
    }
    return this.spans;
  }

  inline(content: readonly Inline[], source: string): void {
    for (const item of content) {
      switch (item.kind) {
        case 'text':
          for (const piece of item.pieces) {
            // A literal run and the unmatched part of a delimiter run are written character for character.
            if (piece.origin === 'literal' || piece.origin === 'delimiter') this.literal(piece.text, piece.from);
            else this.add(piece.text, piece.from, piece.to);
          }
          break;
        case 'soft-break':
          this.add(' ', item.from, item.to);
          break;
        case 'hard-break':
          this.add('\n', item.from, item.to);
          break;
        case 'emphasis':
        case 'strong':
        case 'small-caps':
        case 'link':
          this.inline(item.children, source);
          break;
        case 'brackets':
          this.inline(item.text, source);
          break;
        case 'image':
          this.add(IMAGE_CHARACTER, item.from, item.to);
          break;
        case 'footnote-reference': {
          const number = this.numbers.get(item.from);
          if (typeof number === 'number') this.add(String(number), item.from, item.to);
          else this.orphan(item.from, item.to, source);
        }
      }
    }
  }

  /** An orphan reference reads as its text, escapes and references in the label applied (V12). */
  private orphan(from: number, to: number, source: string): void {
    this.literal('[^', from);
    for (const piece of rawPieces(source, from + 2, to - 1)) {
      if (piece.origin === 'literal') this.literal(piece.text, piece.from);
      else this.add(piece.text, piece.from, piece.to);
    }
    this.literal(']', to - 1);
  }

  /** The text and spans so far, and a fresh start. */
  take(): { text: string; spans: Int32Array } {
    const out = { text: this.parts.join(''), spans: this.spans.slice(0, this.length) };
    this.parts.length = 0;
    this.length = 0;
    return out;
  }
}

/** Every block with visible text, in document order, footnote contents where they stand. */
export function visibleBlocks(chapter: ParsedChapter): VisibleBlock[] {
  const numbers = new Map(indexFootnotes(chapter).references.map((r) => [r.from, r.number]));
  const collector = new Collector(numbers);
  const out: VisibleBlock[] = [];
  const visit = (blocks: readonly Block[]): void => {
    for (const block of blocks) {
      if (block.kind === 'quote' || block.kind === 'footnote') {
        visit(block.blocks);
      } else if (block.kind === 'list') {
        for (const item of block.items) visit(item);
      } else if (block.kind !== 'scene-break') {
        collector.inline(block.content, chapter.text);
        const { text, spans } = collector.take();
        if (text !== '') out.push({ kind: block.kind, from: block.from, to: block.to, text, spans });
      }
    }
  };
  visit(chapterModel(chapter).blocks);
  return out;
}

/**
 * The source range of visible characters `from`–`to` of `block`: from the start of the first to the end of
 * the last. An empty range maps to the point before character `from` (or after the last character).
 */
export function sourceRange(block: VisibleBlock, from: number, to: number): { from: number; to: number } {
  const n = block.text.length;
  if (!Number.isInteger(from) || !Number.isInteger(to) || from < 0 || to < from || to > n) {
    throw new RangeError(`visible range ${from}–${to} is outside 0–${n}`);
  }
  const at = (index: number): number => Number(block.spans[index]);
  if (from === to) {
    const point = from < n ? at(2 * from) : at(2 * n - 1);
    return { from: point, to: point };
  }
  return { from: at(2 * from), to: at(2 * to - 1) };
}
